import {subscribe} from "messaging/queue.js";
import Tone from 'tone';
import {POST_FX, PRE_FX} from 'modulators/constants.js';
import MidiTrigger from './MidiTrigger.js';
import StepData from 'pattern/StepData.js';

export default superclass => class extends superclass  {
    liveInputTrigger(stepData, track, time) {
        this.triggerAttackPreFXEnvelopes(undefined, stepData);
        this.addNoteDown(stepData.transpose);

        if (this.effects[0] && this.effects[0].isRack) {
            this.liveInputTriggerRealTime(stepData, track, time);
            this.addNote(stepData, undefined, time);
        } else if(this.isRealTime()) {
            this.liveInputTriggerRealTime(stepData, track, time);
        } else {
            this.addNote(stepData, undefined, time);
        }
    }


    /**
     * Called when we want to release a note "live" and the midi effects
     * are "realtime", meaning we instantly hear them (versus arpeggiators
     * which wait til the next step to trigger them)
     *
     * Note: When track is undefined, this is triggered by a track's midi effects
     * When track is defined, then this Midi Effects Chain is actually the 
     * global SubSequence Midi effects chain, and we are simply keeping track
     * of which track/instrument is triggering it.
     */
    liveInputTriggerRealTime(stepData, track, time) {
        // need to keep track of what this stepData
        // becomes after going through all the midi effects,
        // so that we can later release them
        
        let transpose = stepData.transpose;
        let stepNumber = this.stepNumber;
        let extraSteps = new Set();
        let _time = time || Tone.Master.context.currentTime;
        for (let i=this.times.length-1; i >= 0; i--) {
            if (_time < this.times[i]) {
                extraSteps.add(Math.max(0, stepNumber));
                stepNumber--;
            } else {
                break;
            }
        }
        stepNumber= Math.max(0, stepNumber);
        extraSteps.add(stepNumber);
        extraSteps = Array.from(extraSteps);
        extraSteps.reverse();

        let triggers = [
            new MidiTrigger(stepData, time, stepNumber)
        ];

        for (let i in this.effects) {
            this.effects[i].onNewStep();
            if (!this.effects[i].isEnabled()) {
                continue;
            }
            for (let j in triggers) {
                this.effects[i].input(triggers[j], true);
            }
            triggers = this.effects[i].output(time, stepNumber, undefined, true, extraSteps);
        }

        this.triggerAttackPostFXEnvelopes(triggers);
        triggers.forEach(
            trigger => {
                trigger.stepData.track = this.track;
                if (track) {
                    track.liveTrigger(trigger.stepData, trigger.stepData.slice, trigger.time);
                } else {
                    this.subSequence.midiEffects.liveInputTrigger(trigger.stepData, this.track, trigger.time);
                }
            }
        );
        
        let noteToTriggers = this.noteToTriggers;
        triggers.forEach(
            t => {
                t.transpose = t.stepData.transpose;
                t.slice = t.stepData.slice;
            });
        if (track) {
            noteToTriggers = this.noteToTriggers[track.trackNumber] || {};
            this.noteToTriggers[track.trackNumber] = noteToTriggers;
        }
        if (noteToTriggers[transpose] !== undefined) {
            noteToTriggers[transpose] = [... noteToTriggers[transpose], ...triggers];
        } else {
            noteToTriggers[transpose] = triggers;
        }
    }

    liveInputRelease(transpose, sliceNumber=0, track) {
        this.removeNoteDown(transpose);
        if (this.effects[0] && this.effects[0].isRack) {
            this.liveInputReleaseRealTime(transpose, sliceNumber, track);
            this.removeNote(transpose, sliceNumber);
        } else if (this.isRealTime()) {
            this.liveInputReleaseRealTime(transpose, sliceNumber, track);
            this.removeNote(transpose, sliceNumber);
        } else {
            this.removeNote(transpose, sliceNumber);
        }
        this.triggerReleasePreFXEnvelopes();
    }

    liveInputReleaseRealTime(transpose, sliceNumber=0, track) {
        let noteToTriggers = this.noteToTriggers;
        if (track) {
            noteToTriggers = this.noteToTriggers[track.trackNumber] || {};
            this.noteToTriggers[track.trackNumber] = noteToTriggers;
        }
        let triggers = noteToTriggers[transpose];
        if (triggers === undefined) {
            let keys = Object.keys(noteToTriggers);
            triggers = noteToTriggers[keys[0]] || [];
            transpose = keys[0];
        }
        let newTriggers = [];
        for (let i in triggers) {
            if (triggers[i].slice === sliceNumber) {
                if (track) {
                    track.liveTriggerRelease(
                        triggers[i].transpose, triggers[i].slice);
                } else {
                    this.subSequence.midiEffects.liveInputRelease(
                        triggers[i].transpose,
                        triggers[i].slice, this.track);
                }
            } else {
                newTriggers.push(triggers[i]);
            }
        }
        if (transpose !== undefined) {
            noteToTriggers[transpose] = newTriggers;
            if (noteToTriggers[transpose].length === 0) {
                delete noteToTriggers[transpose];
            }
        }
    }

    setupMessageReader() {
        if (!this.track || !this.track.subSequence.name) {
            return;
        }
        let prefix = (this.track.trackNumber + 1) + this.track.subSequence.name.toLowerCase();
        let attackType =  prefix + '.triggerAttack';
        let releaseType = prefix + '.triggerRelease';
        let stepData = new StepData();
        stepData.slice = 0;
        stepData.duration = null;
        // stepData.track = this.track;
        if (this.triggerAttackReader) {
            // call to unsubscribe
            this.triggerAttackReader();
        }
        this.triggerAttackReader = subscribe(
            attackType, (o) => {
                let _time = undefined;
                if (typeof o === "object") {
                    let {transpose, time, velocity} = o;
                    stepData.velocity = velocity;
                    stepData.transpose = transpose;
                    _time = time;
                } else {
                    stepData.transpose = o;
                }
                this.liveInputTrigger(stepData, undefined, _time);
            });

        if (this.triggerReleaseReader) {
            // call to unsubscribe
            this.triggerReleaseReader();
        }

        this.triggerReleaseReader = subscribe(
            releaseType, (o) => {
                let transpose = o === "object" ? o.transpose : o;
                this.liveInputRelease(transpose);
            });
    }


    /*
     * The following methods all deal with triggering any envelopes
     * hooked up to this track

     */
    triggerReleasePreFXEnvelopes(time) {
        for (let i in this.track.connectedEnvelopes) {
            let envConnection = this.track.connectedEnvelopes[i];
            let env = envConnection.envelope;
            env.triggerRelease(time);
        }
    }

    triggerAttackPreFXEnvelopes(time, stepData) {
        for (let i in this.track.connectedEnvelopes) {
            let envConnection = this.track.connectedEnvelopes[i];
            if (envConnection.mode === PRE_FX) {
                let env = envConnection.envelope;
                env.triggerAttack(time, stepData.velocity);
            }
        }
    }

    triggerAttackReleasePreFXEnvelopes(time, trigger) {
        let stepData = trigger.stepData;
        for (let i in this.track.connectedEnvelopes) {
            let envConnection = this.track.connectedEnvelopes[i];
            if (envConnection.mode === PRE_FX) {
                let env = envConnection.envelope;
                env.triggerAttackRelease(
                    this.getDuration(stepData.duration),
                    time,
                    stepData.velocity);
            }
        }
    }


    triggerAttackPostFXEnvelopes(triggers) {
        triggers.forEach(trigger => {
            for (let i in this.track.connectedEnvelopes) {
                let envConnection = this.track.connectedEnvelopes[i];
                if (envConnection.mode === POST_FX) {
                    let env = envConnection.envelope;
                    env.triggerAttack(
                        isNaN(trigger.time) ? undefined : trigger.time,
                        trigger.stepData ? trigger.stepData.velocity : 1);
                }
            }
        });
    }

    triggerAttackReleasePostFXEnvelopes(triggers) {
        triggers.forEach(trigger => {
            for (let i in this.track.connectedEnvelopes) {
                let envConnection = this.track.connectedEnvelopes[i];
                if (envConnection.mode === POST_FX) {
                    let env = envConnection.envelope;
                    env.triggerAttackRelease(
                        this.getDuration(trigger.stepData.duration),
                        trigger.time,
                        trigger.stepData.velocity);
                }
            }
        });
    }

    // Used when user does a "live" trigger, i.e. uses a midi 
    // keyboad/computer keyboard to trigger a sampler, synth, etc
    addNote(stepData, time) {
        let triggers = [new MidiTrigger(stepData, time)];
        let effect = undefined;
        for (let i in this.effects) {
            effect = this.effects[i];
            if (!effect.isRealTime()) {
                break;
            }
            this.effects[i].onNewStep();

            for (let j in triggers) {
                effect.input(triggers[j]);
            }
            triggers = this.effects[i].output();
        }
        // this effect will be realTime and hence we want to add the note here
        effect.addNote(triggers[0].stepData.transpose, triggers[0].stepData.slice, this.stepNumber, time);
    }

    removeNote(transpose, sliceNumber) {
        this.effects.forEach(effect => effect.removeNote(transpose, sliceNumber));
    }

    isRealTime() {
        return this.effects
            .filter(effect => effect.isEnabled())
            .every(e => e.isRealTime());
    }

    // returns whether whether sequencer should wait
    // until last possible moment to sequence, in case
    // user triggers something at the last second
    isLiveRealTime() {
        if (!this.track.isEnabled) {
            return false;
        }
        return this.effects.some(e => e.name === 'Arpeggiator');
    }
}
